import './FilterTreeVertical.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { DeferredList } from '@boost-sd/core-js';
import Button from '@components/Button';
import FilterTreeVerticalPlaceholder from '@components/FilterTreeVerticalPlaceholder';
import LoadingIcon from '@components/LoadingIcon';
import { avoidStickyHeader } from '@constants/selector';
import useOnClickOutside from '@hooks/useOnClickOutside';
import useTranslation from '@hooks/useTranslation';
import { useCurrency } from '@providers/CurrencyProvider';
import type { FilterOptionType } from '@providers/FilterProvider';
import {
  getFilterOptionByOptionId,
  hasScopeFilterParam,
  optionIdsOfRefineBy,
  useFilterSettings,
  useFilterState,
  useFilterTreeState,
} from '@providers/FilterProvider';
import { useAdditionalElementThemeSettings } from '@providers/ThemeProvider/Provider/AdditionalElementThemeSettings';
import type { stickyType } from '@utils';
import {
  createClsNameMap,
  getFilterAvoidStickyHeader,
  mapModifiers,
  mergeModifiers,
  sticky,
  toItemArray,
} from '@utils';
import type { ModifiersType } from '@utils/classnames';
import classnames from '@utils/classnames';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import RefineByVertical from '../../components/RefineByVertical';
import FilterOptionByType from '../FilterTree/FilterOptionByType';
import { clsNameMap as toolBarCls } from '../Toolbar';

const productListWithPaginationRoot = `.${classnames(
  'pagination-load-more-container'
)} ,.${classnames('pagination-infinite-scroll-container')}, .${classnames('product-list')}`;

export const clsNameMap = createClsNameMap({
  modifiers: ['hidden', 'left', 'right', 'sticky', 'fullWidth', 'block', 'scroll'],
  elements: {
    'sticky-overlay': createClsNameMap({
      modifiers: ['hidden'],
    }),
    'close-filter': createClsNameMap({
      modifiers: ['left', 'right', 'hidden'],
    }),
    'mobile-toolbar': createClsNameMap({}),
    content: createClsNameMap({}),
    'option-list': createClsNameMap({}),
    'toolbar-middle': createClsNameMap({}),
    back: createClsNameMap({}),
    'toolbar-title': createClsNameMap({}),
    clear: createClsNameMap({
      modifiers: ['hidden'],
    }),
    'clear-all': createClsNameMap({}),
    'show-result': createClsNameMap({}),
    'refine-by': createClsNameMap({}),
    'refine-by-title': createClsNameMap({}),
    'refine-by-heading': createClsNameMap({}),
    'refine-by-list': createClsNameMap({}),
    'refine-by-clear': createClsNameMap({}),
    'refine-by-item': createClsNameMap({}),
    'refine-by-type': createClsNameMap({}),
    'refine-by-option': createClsNameMap({}),
    'refine-by-value': createClsNameMap({}),
  },
})('filter-tree-vertical');

export type FilterTreeVerticalProps = {
  modifiers?: ModifiersType<typeof clsNameMap>;
  isMobile?: boolean;
  customizeSticky?: (stickyData: stickyType) => void;
  filterOptionsShow?: FilterOptionType[];
  disableDeferredRender?: boolean;
};

const FilterTreeVertical = ({
  modifiers,
  isMobile,
  customizeSticky,
  filterOptionsShow,
  disableDeferredRender = true,
}: FilterTreeVerticalProps) => {
  const { t } = useTranslation();

  const { rateCurrency, unitCurrency } = useCurrency();

  const {
    filterData,
    filterParams,
    filterOptionSelected,
    removeFilterParams,
    removeFilterParamsOption,
    setFilterOptionSelected,
    ajaxLoading,
    scopedFilterType,
  } = useFilterState();

  const {
    filterTreeVerticalStyle,
    filterTreeMobileStyle,
    showRefineBy,
    showLoading,
    showMobileLoading,
    formatPipsRange,
    removePriceDecimal,
    stickyFilterOnDesktop,
    stickyFilterOnMobile,
    filterAvoidStickyHeader,
    precisionFormatLabelSlider,
  } = useFilterSettings();

  const { filterTreeState, setFilterTreeVisible } = useFilterTreeState();

  const isFiltering = !filterData || !filterData.data?.filter;

  const modifiersByStyle = () => {
    let _modifiersStyle = {
      left: false, // kết hợp với sticky -> stick & left
      right: false, // kết hợp với sticky -> stick & right
      fullWidth: false, // kết hợp với sticky -> stick & full-width
      sticky: false, // sticky
      block: false, // set max height desktop
      scroll: false, // scroll down
    };

    if (!isMobile) {
      switch (filterTreeVerticalStyle) {
        case 'style-default': // cover expand -> show button
          _modifiersStyle = {
            ..._modifiersStyle,
          };

          break;

        case 'style-off-canvas':
          _modifiersStyle = {
            ..._modifiersStyle,
            left: true,
            sticky: true,
          };
          break;
        case 'style-expand':
          _modifiersStyle = {
            ..._modifiersStyle,
            block: true,
          };
          break;
        default:
          _modifiersStyle = {
            ..._modifiersStyle,
          };
          break;
      }
    } else {
      switch (filterTreeMobileStyle) {
        case 'style2': // default - off canvas
          _modifiersStyle = {
            ..._modifiersStyle,
            right: true,
            sticky: true,
          };

          break;
        case 'style2-fullwidth': // full-width
          _modifiersStyle = {
            ..._modifiersStyle,
            fullWidth: true,
            sticky: true,
          };

          break;
        case 'style1': // expand
          _modifiersStyle = {
            ..._modifiersStyle,
            block: true,
          };

          break;
        case 'style3': // scrolldown offcanvas
          _modifiersStyle = {
            ..._modifiersStyle,
            right: true,
            sticky: true,
            scroll: true,
          };

          break;
        case 'style3-fullwidth': // scrolldown fullwidth
          _modifiersStyle = {
            ..._modifiersStyle,
            fullWidth: true,
            sticky: true,
            scroll: true,
          };

          break;
        default:
          _modifiersStyle = {
            ..._modifiersStyle,
            right: true,
            sticky: true,
          };
          break;
      }
    }

    return _modifiersStyle;
  };

  const isStickyFilterTree = !isMobile
    ? ['style-off-canvas'].includes(filterTreeVerticalStyle)
    : ['style2', 'style2-fullwidth', 'style3', 'style3-fullwidth'].includes(filterTreeMobileStyle);

  const filterTreeElmRef = useRef<HTMLDivElement>(null);
  const filterTreeStickyContainer = useRef<HTMLDivElement>(document.createElement('div'));

  const {
    pagination: { paginationType = 'default' },
  } = useAdditionalElementThemeSettings();

  const stickyVertical = async () => {
    // mobile style:  style1 => Expand mobile
    if (isMobile) {
      if (filterTreeMobileStyle === 'style1') {
        await sticky({
          stickyElement: document.getElementsByClassName(toolBarCls.elm('top-mobile')),
          endElement: productListWithPaginationRoot,
          avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
          isFullWidth: true,
          isMaxWidth: true,
          isToolbarIndex: true,
          customStyle: '',
          isOpeningFilterTree: filterTreeState.filterTreeVisible,
          customizeSticky,
        });

        await sticky({
          stickyElement: document.getElementsByClassName(clsNameMap.elm('content')),
          endElement: 'body',
          avoidElement: getFilterAvoidStickyHeader(
            filterAvoidStickyHeader,
            `${avoidStickyHeader}, .${toolBarCls.elm('top-mobile')}`
          ),
          isFullWidth: true,
          isMaxWidth: true,
          customStyle: 'overflow: auto; padding: 16px;',
          ignoreCheckWidth: true, // because first render - no render filterTree option -> width = 0
          callBack: (isSticky) => {
            isSticky
              ? document.body.classList.add(classnames('sticky-style-expand'))
              : document.body.classList.remove(classnames('sticky-style-expand'));
          },
          customizeSticky,
        });

        return;
      } else {
        await sticky({
          stickyElement: document.getElementsByClassName(toolBarCls.elm('top-mobile')),
          endElement: productListWithPaginationRoot,
          avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
          isFullWidth: true,
          isMaxWidth: true,
          isToolbarIndex: true,
          customStyle: '',
          customizeSticky,
        });

        return;
      }
    } else {
      // desktop style
      switch (filterTreeVerticalStyle) {
        case 'style-default':
          sticky({
            stickyElement: document.getElementsByClassName(clsNameMap.elm('content')),
            endElement: productListWithPaginationRoot,
            avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
            isMaxWidth: true,
            customStyle: `overflow: auto;`,
            callBack: (isSticky) => {
              if (isSticky) {
                if (paginationType === 'default') {
                  document
                    .getElementsByClassName('boost-sd-layout')[0]
                    .setAttribute(
                      'style',
                      `min-height: ${
                        document.getElementsByClassName('boost-sd-left')?.[0]?.scrollHeight
                      }px`
                    );
                } else {
                  document
                    .getElementsByClassName('boost-sd-left')?.[0]
                    ?.setAttribute('style', `position: static;`);
                }
                document.body.classList.add(classnames('filter-tree-vertical-sticky-body'));
              } else {
                document.getElementsByClassName('boost-sd-layout')[0]?.removeAttribute('style');
                document.body.classList.remove(classnames('filter-tree-vertical-sticky-body'));
              }
            },
            customizeSticky,
          });
          break;
        case 'style-expand':
          // sticky toolbar
          await sticky({
            stickyElement: document.getElementsByClassName(toolBarCls.elm('container')),
            endElement: productListWithPaginationRoot,
            avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
            isFullWidth: true,
            isMaxWidth: false,
            isToolbarIndex: true,
            callBack: (isSticky) => {
              if (isSticky) {
                if (paginationType !== 'default') {
                  document
                    .getElementsByClassName('boost-sd-left')?.[0]
                    ?.setAttribute('style', `position: static;`);
                }

                document
                  .getElementsByClassName('boost-sd-layout')[0]
                  .setAttribute(
                    'style',
                    `padding-top: ${
                      document.getElementsByClassName(toolBarCls.elm('container'))[0]?.clientHeight
                    }px;`
                  );

                document
                  .getElementsByClassName(toolBarCls.elm('container'))[0]
                  .classList.add(classnames('toolbar-sticky'));
              } else {
                document.getElementsByClassName('boost-sd-layout')[0]?.removeAttribute('style');
                document
                  .getElementsByClassName(toolBarCls.elm('container'))[0]
                  .classList.remove(classnames('toolbar-sticky'));
              }
            },
            customizeSticky,
          });

          // sticky filter tree content

          await sticky({
            stickyElement: document.getElementsByClassName(clsNameMap.elm('content')),
            endElement: productListWithPaginationRoot,
            avoidElement: getFilterAvoidStickyHeader(
              filterAvoidStickyHeader,
              `${avoidStickyHeader}, .${toolBarCls.elm('container')}`
            ),
            isMaxWidth: true,
            customStyle: `overflow: auto;`,
            ignoreCheckWidth: true, // because first render - no render filterTree option -> width = 0
            callBack: (isSticky) => {
              if (isSticky) {
                document.body.classList.add(classnames('filter-tree-vertical-sticky-body'));
              } else {
                document.body.classList.remove(classnames('filter-tree-vertical-sticky-body'));
                const filterLayout = document.getElementsByClassName(
                  'boost-sd-layout'
                ) as HTMLCollectionOf<HTMLElement>;
                filterLayout[0].style.removeProperty('min-height');
              }
            },
            customizeSticky,
          });

          break;
        default:
          // for other style desktop : style-off-canvas
          sticky({
            stickyElement: document.getElementsByClassName(toolBarCls.elm('container')),
            endElement: productListWithPaginationRoot,
            avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
            isFullWidth: true,
            isMaxWidth: false,
            isToolbarIndex: true,
            callBack: (isSticky) => {
              isSticky
                ? document
                    .getElementsByClassName(toolBarCls.elm('container'))[0]
                    .classList.add(classnames('toolbar-sticky'))
                : document
                    .getElementsByClassName(toolBarCls.elm('container'))[0]
                    .classList.remove(classnames('toolbar-sticky'));

              isSticky
                ? document
                    .getElementsByClassName('boost-sd-right')[0]
                    .setAttribute(
                      'style',
                      `padding-top: ${
                        document.getElementsByClassName(toolBarCls.elm('container'))[0]
                          ?.clientHeight
                      }px`
                    )
                : document.getElementsByClassName('boost-sd-right')[0]?.removeAttribute('style');
            },
            customizeSticky,
          });
          break;
      }
    }
  };

  useEffect(() => {
    if ((!isMobile && stickyFilterOnDesktop) || (isMobile && stickyFilterOnMobile)) {
      stickyVertical();
    }
  }, [isFiltering, filterTreeState.filterTreeVisible]);

  useEffect(() => {
    if (!isMobile) {
      if (filterTreeVerticalStyle === 'style-default') setFilterTreeVisible(true);
    } else {
      filterTreeState.filterTreeVisible && setFilterTreeVisible(false);
    }
  }, [isMobile]);

  useOnClickOutside(filterTreeElmRef, () => {
    if (filterTreeState.filterTreeVisible && isStickyFilterTree) {
      setFilterTreeVisible(false);
    }
  });

  useEffect(() => {
    if (
      modifiersByStyle().sticky &&
      filterTreeState.filterTreeVisible &&
      (isMobile || (!isMobile && filterTreeVerticalStyle === 'style-off-canvas'))
    ) {
      document.body.classList.add(classnames('g-no-scroll'));
    } else {
      document.body.classList.remove(classnames('g-no-scroll'));
    }

    if (isMobile && stickyFilterOnMobile) {
      if (filterTreeState.filterTreeVisible) {
        document.body.classList.add(classnames('sticky-opening'));
      } else {
        document.body.classList.remove(classnames('sticky-opening'));
      }
    }
  }, [filterTreeState.filterTreeVisible]);

  const getCurrentModifierType = () => {
    if (!isMobile) {
      return 'collapsed';
    } else if (isMobile) {
      switch (filterTreeMobileStyle) {
        case 'style2':
        case 'style2-fullwidth':
          return 'singleOption';
        default:
          return 'collapsed';
      }
    }

    return 'collapsed';
  };

  if (isStickyFilterTree && !filterTreeStickyContainer.current.parentNode) {
    document.body.appendChild(filterTreeStickyContainer.current);
  }

  const renderRefineBy = () => {
    const isRender =
      showRefineBy &&
      !filterParamsClearAll() &&
      hasScopeFilterParam(filterData, filterParams, scopedFilterType) &&
      (!isMobile || (isMobile && modifiersByStyle().block));

    if (!isRender) return <></>;

    const refineByData: {
      filterOptionId: string;
      rawValue: string;
      label: string | undefined;
      value: string | undefined;
    }[] = [];

    optionIdsOfRefineBy(filterParams)?.forEach((filterOptionId) => {
      toItemArray(filterParams[filterOptionId])?.forEach((value: string) => {
        const { refineByLabel: _label, refineByValue: _value } = getFilterOptionByOptionId(
          filterData,
          filterOptionId,
          value,
          rateCurrency,
          unitCurrency,
          formatPipsRange,
          removePriceDecimal,
          { precisionFormatLabelSlider }
        );

        refineByData.push({
          filterOptionId,
          rawValue: value,
          label: _label,
          value: _value,
        });
      });
    });

    return (
      <RefineByVertical
        refineByData={refineByData}
        onClearAll={() => removeFilterParamsOption('', 'all')}
        onRemove={(filterOptionId: string, value: string) =>
          removeFilterParams(filterOptionId, value)
        }
      />
    );
  };

  const filterParamsClearAll = () => {
    return optionIdsOfRefineBy(filterParams)?.length === 0;
  };

  const renderButtonCloseOrBack = () => {
    if (modifiersByStyle().fullWidth && (!filterOptionSelected || modifiersByStyle().scroll)) {
      return (
        <Button
          aria-label={t('Close filter tree')}
          buttonModifiers={['close']}
          onClick={() => setFilterTreeVisible(false)}
          hasSpan={false}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='7'
            height='12'
            role='img'
            viewBox='0 0 7 12'
            fill='none'
            aria-labelledby={t('close')}
          >
            <title id={t('close')} hidden>
              Close
            </title>
            <path
              d='M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z'
              fill='#222222'
            />
          </svg>
        </Button>
      );
    } else if (!modifiersByStyle().scroll && filterOptionSelected) {
      return (
        <Button
          aria-label={t('back')}
          buttonModifiers={['back']}
          onClick={() => setFilterOptionSelected(null)}
          hasSpan={false}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='7'
            height='12'
            role='img'
            viewBox='0 0 7 12'
            fill='none'
            aria-labelledby={t('back')}
          >
            <title id={t('back')} hidden>
              Back
            </title>
            <path
              d='M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z'
              fill='#222222'
            />
          </svg>
        </Button>
      );
    }

    return <Button aria-label={t('back')} buttonModifiers={['hidden', 'back']}></Button>;
  };

  const renderButtonClearOrClearAll = () => {
    if ((modifiersByStyle().scroll || !filterOptionSelected) && !filterParamsClearAll()) {
      return (
        <Button buttonModifiers={['clear']} onClick={() => removeFilterParamsOption('', 'all')}>
          {t('clearAll')}
        </Button>
      );
    } else if (
      filterOptionSelected &&
      !modifiersByStyle().scroll &&
      filterParams[filterOptionSelected.filterOptionId]
    ) {
      return (
        <Button
          buttonModifiers={['clear']}
          onClick={() => removeFilterParamsOption(filterOptionSelected.filterOptionId, '')}
        >
          {t('clear')}
        </Button>
      );
    }

    return <></>;
  };

  const isLoadingIcon =
    (filterData.loading || ajaxLoading) &&
    ((!isMobile && showLoading) || (isMobile && showMobileLoading));

  const isShowMobileToolbar =
    isMobile && (modifiersByStyle().sticky || modifiersByStyle().fullWidth);

  const isShowCloseFilterButton =
    modifiersByStyle().left ||
    modifiersByStyle().right ||
    modifiersByStyle().sticky ||
    modifiersByStyle().scroll;

  const render = () => {
    const modifierType = getCurrentModifierType();
    const filterTreeModifierType = modifiersByStyle();

    /**
     * This is the condition for refineBy to display.
     * showRefineBy: This is the refineBy setting that is turned off in the app admin.
     * filterParamsClearAll(): checks to see if all filter conditions have been cleared. Only show if it's not completely clear.
     * hasScopeFilterParam(filterData, filterParams, scopedFilterType): check if filtering by scope params is happening or not
     * (!isMobile || (isMobile && filterTreeMobileStyle === 'style1')): RefineBy only shows on desktop or mobile style1
     */
    const isRenderRefineBy =
      showRefineBy &&
      !filterParamsClearAll() &&
      hasScopeFilterParam(filterData, filterParams, scopedFilterType) &&
      (!isMobile || (isMobile && filterTreeMobileStyle === 'style1'));

    const isHiddenVertical =
      !filterTreeState.filterTreeVisible ||
      (!filterData.loading && filterOptionsShow?.length === 0 && !isRenderRefineBy);

    return (
      <>
        <div
          className={mergeModifiers(clsNameMap, [
            modifiers,
            {
              hidden: isHiddenVertical,
            },
            filterTreeModifierType,
          ])}
          ref={filterTreeElmRef}
          style={
            filterTreeModifierType.sticky
              ? {
                  ...(filterTreeState.filterTreeVisible
                    ? { animation: 'filter-tree-sticky-open 400ms ease' }
                    : {
                        animation: 'filter-tree-sticky-close 400ms ease',
                        animationFillMode: 'forwards',
                      }),
                }
              : {}
          }
        >
          {isShowCloseFilterButton && (
            <div
              className={mapModifiers(clsNameMap['close-filter'], {
                left: modifiersByStyle().left,
                right: modifiersByStyle().right,
                hidden: !filterTreeState.filterTreeVisible,
              })}
            >
              <Button
                aria-label={t('Close filter tree')}
                buttonModifiers={['close']}
                onClick={() => setFilterTreeVisible(false)}
                hasSpan={false}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='40'
                  height='40'
                  viewBox='0 0 40 40'
                  fill='none'
                  aria-labelledby={t('close')}
                >
                  <title id={t('close')} hidden>
                    Close
                  </title>
                  <rect width='40' height='40' fill='#222222' />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M21.4143 20L27.7072 13.707C28.0982 13.316 28.0982 12.684 27.7072 12.293C27.3162 11.902 26.6842 11.902 26.2933 12.293L20.0002 18.586L13.7072 12.293C13.3162 11.902 12.6842 11.902 12.2932 12.293C11.9022 12.684 11.9022 13.316 12.2932 13.707L18.5862 20L12.2932 26.293C11.9022 26.684 11.9022 27.316 12.2932 27.707C12.4882 27.902 12.7442 28 13.0002 28C13.2563 28 13.5122 27.902 13.7072 27.707L20.0002 21.414L26.2933 27.707C26.4882 27.902 26.7443 28 27.0002 28C27.2562 28 27.5122 27.902 27.7072 27.707C28.0982 27.316 28.0982 26.684 27.7072 26.293L21.4143 20Z'
                    fill='white'
                  />
                  <mask
                    id='mask0_21_1009'
                    maskUnits='userSpaceOnUse'
                    x='12'
                    y='11'
                    width='17'
                    height='17'
                  >
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M21.4143 20L27.7072 13.707C28.0982 13.316 28.0982 12.684 27.7072 12.293C27.3162 11.902 26.6842 11.902 26.2933 12.293L20.0002 18.586L13.7072 12.293C13.3162 11.902 12.6842 11.902 12.2932 12.293C11.9022 12.684 11.9022 13.316 12.2932 13.707L18.5862 20L12.2932 26.293C11.9022 26.684 11.9022 27.316 12.2932 27.707C12.4882 27.902 12.7442 28 13.0002 28C13.2563 28 13.5122 27.902 13.7072 27.707L20.0002 21.414L26.2933 27.707C26.4882 27.902 26.7443 28 27.0002 28C27.2562 28 27.5122 27.902 27.7072 27.707C28.0982 27.316 28.0982 26.684 27.7072 26.293L21.4143 20Z'
                      fill='white'
                    />
                  </mask>
                  <g mask='url(#mask0_21_1009)'></g>
                </svg>
              </Button>
            </div>
          )}
          {isShowMobileToolbar && (
            <div className={clsNameMap.elm('mobile-toolbar')}>
              {renderButtonCloseOrBack()}

              <div className={clsNameMap.elm('toolbar-middle')}>
                {filterOptionSelected && !modifiersByStyle().scroll
                  ? filterOptionSelected.label
                  : t('refineMobile')}
              </div>

              {renderButtonClearOrClearAll()}
            </div>
          )}

          {isFiltering ? (
            <FilterTreeVerticalPlaceholder />
          ) : (
            <div className={clsNameMap.elm('content')} aria-label={t('productFilter')}>
              {showRefineBy && renderRefineBy()}
              <div className={clsNameMap.elm('option-list')}>
                {filterOptionsShow && filterTreeState.filterTreeVisible && (
                  <DeferredList
                    delay={isMobile ? 0 : 50}
                    items={filterOptionsShow}
                    runOnceWhenItemsAvailable
                    disableDeferredRender={disableDeferredRender}
                    onItemRender={(option) => {
                      return (
                        <FilterOptionByType
                          key={option.filterOptionId}
                          option={option}
                          isMobile={isMobile}
                          currentModifierType={modifierType}
                        />
                      );
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {modifiersByStyle().sticky && (
            <div
              className={mapModifiers(clsNameMap['show-result'], {
                hidden: !filterOptionSelected,
              })}
            >
              <Button buttonModifiers={['result']} onClick={() => setFilterTreeVisible(false)}>
                {t('showResult') + ` (${filterData.data?.total_product})`}
              </Button>
            </div>
          )}
          {isLoadingIcon && <LoadingIcon modifiers={['filter']} />}
        </div>
      </>
    );
  };

  if (isStickyFilterTree) {
    return createPortal(
      <div
        style={
          filterTreeState.filterTreeVisible
            ? { animation: 'filter-tree-sticky-overlay-open 250ms linear' }
            : {
                animation: 'filter-tree-sticky-overlay-close 250ms linear',
                animationFillMode: 'forwards',
              }
        }
        className={mapModifiers(clsNameMap['sticky-overlay'])}
      >
        {render()}
      </div>,
      filterTreeStickyContainer.current
    );
  }

  return <>{render()}</>;
};

export default registryComponent('FilterTreeVertical', FilterTreeVertical);
